import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Picker } from 'emoji-mart';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { EmojiData } from 'emoji-mart';

interface Props {
  buttonColor: string;
  buttonColorActive: string;
  disabled: boolean;
  isChat?: boolean;
  onSelect: (emoji: EmojiData) => void;
}

const ReaderEmojiPicker = ({
  buttonColor,
  buttonColorActive,
  disabled,
  isChat,
  onSelect
}: Props) => {
  const [position, setPosition] = useState({ x: 50, y: 95 });
  const [visible, setVisible] = useState(false);

  const { messages } = useReduxState();

  const locales = {
    search: messages.commonSearch,
    clear: messages.commonClear,
    notfound: messages.readerChatEmojiNotFound,
    categories: {
      search: messages.readerChatEmojiCategorySearch,
      recent: messages.readerChatEmojiCategoryRecent,
      people: messages.readerChatEmojiCategoryPeople,
      nature: messages.readerChatEmojiCategoryNature,
      foods: messages.readerChatEmojiCategoryFoods,
      activity: messages.readerChatEmojiCategoryActivity,
      places: messages.readerChatEmojiCategoryPlaces,
      objects: messages.readerChatEmojiCategoryObjects,
      symbols: messages.readerChatEmojiCategorySymbols,
      flags: messages.readerChatEmojiCategoryFlags,
      custom: messages.readerChatEmojiCategoryCustom
    }
  };

  const pickerRootElement = useRef(undefined);
  useEffect(() => {
    pickerRootElement.current = document.querySelector('.reader-emoji-root');
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const handleButtonClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.stopPropagation();

    if (!visible) {
      document.addEventListener('click', handleOutsideClick);
      setVisible(true);

      if (!isChat) {
        const height = document.documentElement.clientHeight;
        const bottom = height - ev.clientY;

        setPosition({
          x: document.documentElement.clientWidth - ev.clientX + 10,
          y:
            bottom + 500 > height
              ? height - 500
              : Math.abs(ev.clientY - document.documentElement.clientHeight)
        });
      }
    }
  };

  const handleOutsideClick = (ev: MouseEvent) => {
    // check if click came from inside picker
    let el = ev.target as HTMLElement;
    while (el && el.classList && !el.classList.contains('emoji-mart')) {
      el = el.parentElement;
    }

    if (el && el.classList && el.classList.contains('emoji-mart')) return;

    // hide picker
    document.removeEventListener('click', handleOutsideClick);
    setVisible(false);
  };

  const handleSelect = (item: EmojiData) => {
    document.removeEventListener('click', handleOutsideClick);
    setVisible(false);
    onSelect(item);
  };

  return (
    <div className="picker">
      <button
        aria-label={messages.readerChatEmojiAdd}
        className="button"
        disabled={disabled}
        onClick={handleButtonClick}
        type="button"
      >
        <Icon icon={['far', 'smile']} />
      </button>

      {visible
        ? ReactDOM.createPortal(
            <Picker
              emoji=""
              i18n={locales}
              onSelect={handleSelect}
              perLine={8}
              set="apple"
              style={{
                position: 'fixed',
                bottom: position.y,
                right: position.x,
                zIndex: 60
              }}
              title=""
            />,
            pickerRootElement.current
          )
        : null}

      <style jsx>{`
        .picker {
          display: inline-block;
        }
        .button {
          background-color: transparent;
          border: 0;
          color: ${buttonColor};
          cursor: pointer;
          font-size: 1.4rem;
          padding: 8px 10px;
          transition: color 0.3s;
        }
        .button:hover {
          color: ${buttonColorActive};
        }
        .button:disabled {
          cursor: default;
          opacity: 0.5;
        }
        .button:disabled:hover {
          color: ${buttonColor};
        }
      `}</style>
    </div>
  );
};

export default ReaderEmojiPicker;
